import { useState, useCallback, JSX } from "react"
import { Pencil } from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Survey, addReportToSurvey } from "@/services/surveysListService"
import { useAuth } from "@/hooks/useAuth"
import { useFileUpload } from "@/hooks/use-file-upload.ts"
import { addReportToBiblio, NewReportRequest } from "@/services/biblioService"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip.tsx"
import { ReportFormFields } from "@/components/survey-catalogue/ReportFormFields"


/**
 * Props for the UpdateReportDialog component.
 *
 * @property onReportCreated - Callback invoked after the report is successfully
 *   created and linked to the survey. The parent typically re-fetches surveys.
 * @property survey - The survey to which the report will be linked.
 */
interface UpdateReportDialogProps {
  onReportCreated: () => void
  survey: Survey
}


/**
 * Dialog component for updating a surveys report.
 *
 * Collects a report file (required) and metadata (title required, plus optional
 * fields like authors, publication year, etc.). On submission it creates a biblio
 * record via addReportToBiblio, then links the resulting sourceId and Google Drive
 * file to the survey via addReportToSurvey.
 *
 * @param props - Component props.
 * @param props.onReportCreated - Callback fired after successful report creation.
 * @param props.survey - The survey this report will be attached to.
 * @returns The rendered dialog trigger and content.
 */
export default function UpdateReportDialog({ onReportCreated, survey }: UpdateReportDialogProps): JSX.Element {
  const { getAccessToken, user } = useAuth()

  const [{ files }, { openFileDialog, removeFile, getInputProps, clearFiles }] = useFileUpload({
    accept: "*",
    multiple: false,
  })

  // Dialog open state
  const [open, setOpen] = useState<boolean>(false)

  // Form state — required fields
  const [title, setTitle] = useState<string>("")

  // Form state — optional fields
  const [publicationYear, setPublicationYear] = useState<string>("")
  const [authors, setAuthors] = useState<string>("")
  const [institutions, setInstitutions] = useState<string>("")
  const [city, setCity] = useState<string>("")
  const [reportNumber, setReportNumber] = useState<string>("")
  const [pages, setPages] = useState<string>("")

  // Submission state
  const [submitting, setSubmitting] = useState<boolean>(false)
  const [submitError, setSubmitError] = useState<string | null>(null)


  /**
   * Reset all form fields to their initial empty state.
   */
  const resetForm = useCallback((): void => {
    setTitle("")
    setPublicationYear("")
    setAuthors("")
    setInstitutions("")
    setCity("")
    setReportNumber("")
    setPages("")
    setSubmitError(null)
  }, [])

  /**
   * Handle form submission.
   */
  const handleSubmit = useCallback(async (): Promise<void> => {
    setSubmitError(null)

    setSubmitting(true)
    try {
      const token = await getAccessToken()

        // Create a new biblio source
        if (!title.trim()) {
          setSubmitError("Title is required.")
          setSubmitting(false)
          return
        }
        if (files.length === 0) {
          setSubmitError("A report file is required when creating a new source.")
          setSubmitting(false)
          return
        }

        const authorsList: string[] = authors
          .split(";")
          .map(author => author.trim())
          .filter(author => author.length > 0)

        const institutionsList: string[] = institutions
          .split(",")
          .map(institution => institution.trim())
          .filter(institution => institution.length > 0)

        const newReportRequest: NewReportRequest = {
          title: title.trim(),
          files,
          mapSurveyId: survey.mapSurveyId,
          surveyType: survey.surveyType,
          publicationYear: publicationYear.trim(),
          authors: authorsList,
          institutions: institutionsList,
          reportNumber: reportNumber.trim(),
          pages: pages.trim(),
          city: city.trim(),
          createdBy: user?.email ?? "unknown",
        }

        console.log(newReportRequest)

        const reportResponse = await addReportToBiblio(token, newReportRequest)

        console.log(reportResponse)

        const sourceFileId: string | undefined = reportResponse.driveFileId
          ? `https://drive.google.com/file/d/${reportResponse.driveFileId}/view`
          : undefined

        await addReportToSurvey(token, {
          sourceId: String(reportResponse.sourceId),
          sourceFileId,
          mapSurveyId: survey.mapSurveyId,
          surveyType: survey.surveyType,
        })

      resetForm()
      clearFiles()
      setOpen(false)
      onReportCreated()
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : "Failed to add report")
    } finally {
      setSubmitting(false)
    }
  }, [
    title,
    files,
    authors,
    institutions,
    publicationYear,
    reportNumber,
    pages,
    city,
    survey,
    user,
    getAccessToken,
    resetForm,
    clearFiles,
    onReportCreated,
  ])

  return (
    <Dialog
      open={open}
      onOpenChange={isOpen => {
        // Prevent closing the dialog while a submission is in progress
        if (!isOpen && submitting) return
        setOpen(isOpen)
        if (!isOpen) {
          resetForm()
          clearFiles()
        }
      }}
    >
      <Tooltip>
        <TooltipTrigger asChild>
          <DialogTrigger asChild>
            <Button variant="outline" size="icon" className="size-7">
              <Pencil className="size-3.5" />
            </Button>
          </DialogTrigger>
        </TooltipTrigger>
        <TooltipContent>Update survey report file</TooltipContent>
      </Tooltip>
      <DialogContent
        className="sm:max-w-lg max-h-[85vh] overflow-y-auto"
        showCloseButton={!submitting}
        onPointerDownOutside={(e) => { if (submitting) e.preventDefault() }}
        onEscapeKeyDown={(e) => { if (submitting) e.preventDefault() }}
      >
        <div className="grid gap-4 py-4">
          <p>Update Survey Report File</p>
          <ReportFormFields
            title={title}
            setTitle={setTitle}
            publicationYear={publicationYear}
            setPublicationYear={setPublicationYear}
            authors={authors}
            setAuthors={setAuthors}
            institutions={institutions}
            setInstitutions={setInstitutions}
            city={city}
            setCity={setCity}
            reportNumber={reportNumber}
            setReportNumber={setReportNumber}
            pages={pages}
            setPages={setPages}
            files={files}
            openFileDialog={openFileDialog}
            removeFile={removeFile}
            getInputProps={getInputProps}
          />
        </div>

        {/* Error display */}
        {submitError && (
          <p className="text-sm text-destructive" role="alert">
            {submitError}
          </p>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => {
              resetForm()
              clearFiles()
              setOpen(false)
            }}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={submitting}>
            {submitting ? "Updating Report…" : "Update Report"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
